
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Calendar } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import type { JogoBichoResultado, JogoBichoConfig } from "@/types/softwareTurbinado";

type ResultadosTableProps = {
  resultados: JogoBichoResultado[];
  config: JogoBichoConfig | null;
  selectedDays: number;
  onAddResult: () => void;
};

const ResultadosTable = ({ resultados, config, selectedDays, onAddResult }: ResultadosTableProps) => {
  const isMobile = useIsMobile();
  
  // Ordenar resultados por data (mais recente primeiro) e limitar aos dias selecionados
  const sortedResultados = [...resultados].sort((a, b) => 
    new Date(b.data).getTime() - new Date(a.data).getTime()
  ).slice(0, selectedDays);

  const formatDate = (dateString: string) => {
    if (!dateString) return '--/--/----';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatResult = (result: string) => {
    if (!result || result === '----') return '----';
    return result;
  };

  const renderHorarioColumn = (resultado: JogoBichoResultado, horario: string, titulo: string, bgColor: string) => {
    const fields = Array.from({ length: 5 }, (_, i) => `${horario}_${i + 1}`);
    
    return (
      <div key={horario} className="flex flex-col items-center">
        <div className={`${bgColor} text-center font-bold text-xs py-2 px-2 sm:px-3 rounded-t-lg min-w-[80px] sm:min-w-[100px] border-b-2 border-gray-300`}>
          {titulo}
        </div>
        <div className="flex flex-col gap-1 bg-white border-l border-r border-b border-gray-300 rounded-b-lg p-1 sm:p-2 min-w-[80px] sm:min-w-[100px]">
          {fields.map((field, index) => (
            <div key={field} className="flex items-center gap-1">
              <span className="text-xs font-medium text-gray-600 w-4 sm:w-6">
                {index + 1}º
              </span>
              <div className="flex-1 bg-gray-50 border rounded text-center font-mono text-xs py-1 px-1 sm:px-2 text-gray-900 font-semibold">
                {formatResult(resultado[field as keyof JogoBichoResultado] as string)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderResultadoCard = (resultado: JogoBichoResultado, index: number) => {
    const isPlaceholder = resultado.id.startsWith('placeholder');
    
    return (
      <Card key={resultado.id} className="bg-yellow-50 border-2 border-yellow-300 w-full mb-4">
        <CardHeader className="pb-3">
          <CardTitle className="text-center text-base sm:text-lg font-bold bg-yellow-200 py-2 px-4 rounded text-gray-900">
            DIA {index + 1} DE {selectedDays}
          </CardTitle>
          <div className="text-center text-sm font-medium text-gray-700">
            {isPlaceholder ? 'Sem resultado' : formatDate(resultado.data)}
          </div>
        </CardHeader>
        <CardContent>
          {/* Layout horizontal dos horários organizados da esquerda para direita */}
          <div className={`${isMobile ? 'grid grid-cols-2 gap-2' : 'flex flex-wrap justify-center gap-3'}`}>
            {renderHorarioColumn(resultado, 'ppt', config?.titulo_ppt || 'PPT', 'bg-blue-200 text-blue-800')}
            {renderHorarioColumn(resultado, 'ptm', config?.titulo_ptm || 'PTM', 'bg-green-200 text-green-800')}
            {renderHorarioColumn(resultado, 'pt', config?.titulo_pt || 'PT', 'bg-red-200 text-red-800')}
            {renderHorarioColumn(resultado, 'ptv', config?.titulo_ptv || 'PTV', 'bg-purple-200 text-purple-800')}
            {renderHorarioColumn(resultado, 'ptn', config?.titulo_ptn || 'PTN', 'bg-orange-200 text-orange-800')}
            {renderHorarioColumn(resultado, 'coruja', config?.titulo_coruja || 'CORUJA', 'bg-gray-200 text-gray-800')}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Preencher com placeholders se não tiver resultados suficientes
  const displayResults = [...sortedResultados];
  while (displayResults.length < selectedDays) {
    displayResults.push({
      id: `placeholder-${displayResults.length}`,
      user_id: '',
      data: '',
      ppt_1: '----', ppt_2: '----', ppt_3: '----', ppt_4: '----', ppt_5: '----',
      ptm_1: '----', ptm_2: '----', ptm_3: '----', ptm_4: '----', ptm_5: '----',
      pt_1: '----', pt_2: '----', pt_3: '----', pt_4: '----', pt_5: '----',
      ptv_1: '----', ptv_2: '----', ptv_3: '----', ptv_4: '----', ptv_5: '----',
      ptn_1: '----', ptn_2: '----', ptn_3: '----', ptn_4: '----', ptn_5: '----',
      coruja_1: '----', coruja_2: '----', coruja_3: '----', coruja_4: '----', coruja_5: '----',
      created_at: '',
      updated_at: ''
    });
  }

  return (
    <Card className="shadow-lg">
      <CardHeader className="pb-4 bg-gradient-to-r from-blue-50 to-blue-100">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <CardTitle className="text-lg sm:text-xl lg:text-2xl flex items-center gap-2 text-center sm:text-left text-gray-900 dark:text-gray-100 font-bold">
            <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            RESULTADOS DAS LOTERIAS
          </CardTitle>
          <Button 
            onClick={onAddResult} 
            className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-sm sm:text-base w-full sm:w-auto"
          >
            <Plus className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
            Inserir {selectedDays} Dias
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-2 sm:p-3 lg:p-6">
        <div className="space-y-4">
          {displayResults.map((resultado, index) => renderResultadoCard(resultado, index))}
        </div>
        
        <div className="mt-6 sm:mt-8 p-3 sm:p-4 lg:p-6 bg-gray-50 text-center border-t">
          <p className="text-sm sm:text-base lg:text-lg font-medium text-gray-700 mb-3">
            Use o formulário multietapas para inserir os resultados dos últimos {selectedDays} dias de forma organizada
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-3 lg:gap-6 text-xs sm:text-sm text-gray-600">
            <span className="bg-white px-2 sm:px-3 py-1 rounded-full border whitespace-nowrap">
              <strong>Resultados:</strong> {sortedResultados.length}/{selectedDays}
            </span>
            <span className="bg-white px-2 sm:px-3 py-1 rounded-full border whitespace-nowrap">
              <strong>Estado:</strong> {config?.estado || 'Rio de Janeiro'}
            </span>
            <span className="bg-white px-2 sm:px-3 py-1 rounded-full border whitespace-nowrap">
              <strong>Loteria:</strong> {config?.loteria || 'Look'}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ResultadosTable;
